import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { odrApi, odrErr, label, ODR_TOKEN_KEY } from "@/components/odr/odrApi";
import { OdrCaseView } from "@/pages/ODRDetail";

// Public: accept a case-scoped invitation and receive a limited ODR session (no organisation membership required).
export default function ODRJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { odrApi.peekInvite(token).then(setInv).catch((e) => setErr(e?.response?.data?.detail || "Invalid invitation")); }, [token]);
  const accept = async () => {
    setBusy(true);
    try { const r = await odrApi.acceptInvite({ token, name, acceptTerms: agree }); localStorage.setItem(ODR_TOKEN_KEY, r.token); toast.success("Welcome. You now have case-scoped access."); navigate(`/odr/portal/${r.caseId}`); }
    catch (e) { odrErr(e); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-surface">
      <PortalHeader />
      <div className="mx-auto max-w-lg px-4 py-12">
        {err && <div className="rounded-xl border border-danger/40 bg-card p-6 text-center" data-testid="join-error"><div className="font-display text-xl text-ink">Invitation unavailable</div><p className="mt-1 text-sm text-ink-2">{err}</p></div>}
        {inv && <div className="rounded-xl border border-line bg-card p-6" data-testid="join-card">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-copper">You are invited</div>
          <h1 className="font-display text-2xl font-semibold text-ink">{inv.caseTitle}</h1>
          <div className="mt-1 text-sm text-ink-2">{inv.caseRef || "Reference pending"} · as <b>{label(inv.role)}</b> ({label(inv.side)}) · invitation for {inv.email}</div>
          <p className="mt-3 text-xs text-ink-2">Why you are invited: a dispute has been referred to online resolution and you are a named participant. Your access is limited to this case and your role's confidentiality contexts. Private caucus and party-private material of the other side are never visible to you.</p>
          <div className="mt-4 space-y-3"><Input placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="join-name" />
            <label className="flex items-start gap-2 text-xs text-ink-2"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="join-terms" /> I accept the participation terms, confirm I am the addressee of this invitation, and understand that consents to the process itself are recorded separately inside the case.</label>
            <Button className="w-full" disabled={!agree || busy} onClick={accept} data-testid="join-accept-btn">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1 h-4 w-4" />} Verify & enter case</Button></div>
          <div className="mt-2 text-center text-[11px] text-ink-muted">Expires {String(inv.expiresAt).slice(0, 10)}</div>
        </div>}
      </div>
    </div>
  );
}

function PortalHeader() {
  const navigate = useNavigate();
  const signOut = () => { localStorage.removeItem(ODR_TOKEN_KEY); navigate("/login"); };
  return <header className="flex items-center justify-between border-b border-line bg-navy px-6 py-3 text-white"><div className="flex items-center gap-2 font-display text-lg text-gold"><img src="/logo-mark.png" alt="" className="h-8 w-8 rounded" /> NYAYYAKSHA ODR</div><div className="flex items-center gap-2 text-xs"><span className="rounded-full bg-white/10 px-2 py-0.5">Participant access</span><button onClick={signOut} className="hover:text-copper" data-testid="portal-signout">Sign out</button></div></header>;
}

export function ODRPortal() {
  const { id } = useParams();
  const navigate = useNavigate();
  if (!localStorage.getItem(ODR_TOKEN_KEY)) return <div className="p-10 text-center text-sm text-ink-2">Your participant session has ended. Use your invitation link to re-enter.</div>;
  return <div className="min-h-screen bg-surface"><PortalHeader /><main className="mx-auto max-w-6xl px-6 py-6" data-testid="odr-portal"><OdrCaseView id={id} matters={null} onExit={() => navigate("/login")} /></main></div>;
}
